<template>
<m-pageframe
    title="Organization Billing Settings: {organization_label}"
>
<v-container
    v-if="rate_card != null"
>

    <h2>Billing Settings</h2>

    <m-edit-text
        label="Organization Name"
        :value="rate_card.organization_label"
        read_only
    />

    <m-edit-text
        :value="rate_card_label"
        label="Rate Card"
        :custom_on_edit="onClickEditRateCard"
    />


    <m-edit-textarea
        :value="rate_card.selected_rate_card.custom_rate_card_label"
        label="Rate Card Custom Description / Notes"
        v-on:input="onSetCustomRateCardLabel"
        optional
    />

    <m-edit-textarea
        :value="rate_card.selected_rate_card.free_trial_desc"
        label="Free Trial Description / Notes"
        v-on:input="onSetFreeTrialDesc"
        optional
    />

    <m-edit-text
        :value="intuit_company_name"
        label="Intuit Company Name (id)"
        :custom_on_edit="onClickEditIntuitCustomer"
        :read_only="!is_finance"
    />

    <m-intuit-search-customer-dialog
        ref="search-customers-dialog"
        v-on:input="onSetIntuitCustomer"
    />  

    <m-edit-textarea
        :value="rate_card.selected_rate_card.po_number"
        label="Purchase Order (PO) Number"
        v-on:input="onSetPONumber"
        optional
        :read_only="!is_finance"
    />
    

    <m-edit-text
        :value="pretty_customer_emails"
        label="Email Invoice To (customer emails only)"
        v-on:input="onSetCustomerEmails"
        hint="Separate multiple emails using a comma or semi-colon"
        optional
        :read_only="!is_finance"
    />
    
    <m-edit-select
        label="Payment Method"
        :value="rate_card.selected_rate_card.payment_method"
        :possible_values="$rc.enumChoices('red_chirp_payment_method',false)"
        v-on:input="onSetPaymentMethod"
        :read_only="!is_finance"
    />
      

    <m-edit-select
        label="Payment Terms"
        :value="rate_card.selected_rate_card.payment_terms"
        :possible_values="$rc.enumChoices('red_chirp_payment_terms',false)"
        v-on:input="onSetPaymentTerms"
        :read_only="!is_finance"
    />

    <m-edit-date
        label="Billing Start Date"
        :value="rate_card.selected_rate_card.billing_start_date"
        v-on:input="onSetBillingStartDate"
        optional
        :read_only="!is_finance"
    />

    <m-edit-text
        label="Credit for Setup Fee"
        :value="$rc.getDeep(rate_card, 'selected_rate_card/credit_setup/pretty_description')"
        :custom_on_edit="onClickEditCredit"
        :read_only="!is_finance"
    />

    <m-edit-text
        label="Credit for Recurring Fee"
        :value="$rc.getDeep(rate_card, 'selected_rate_card/credit_recurring_fee/pretty_description')"
        :custom_on_edit="onClickEditCredit"
        :read_only="!is_finance"
    />

    <v-checkbox
        label="Invoice Exception"
        v-model="rate_card.selected_rate_card.invoice_exception"
        v-on:change="onSetInvoiceException"
        :disabled="!is_finance"
    />

    <m-edit-textarea
        v-if="rate_card.selected_rate_card.invoice_exception"
        label="Invoice Exception Notes"
        :value="rate_card.selected_rate_card.invoice_exception_note"
        v-on:input="onSetInvoiceExceptionNote"
        optional
        :read_only="!is_finance"
    />

    <div
        v-show="is_finance"
    >

        <h2>Preview &amp; Create Invoice</h2>

        <m-invoice-period-selector
            v-model="selected_month"
            v-on:input="updateInvoicePreview"
        />

        <div
            v-if="msg_count_comparison != null"
            style="margin-bottom: 15px;"
        >
            <b>SMS (billed vs stored)</b>: {{msg_count_comparison.sms_delta_pct_pretty}} ({{msg_count_comparison.sms_delta}})<br/>
            <b>MMS (billed vs stored)</b>: {{msg_count_comparison.mms_delta_pct_pretty}} ({{msg_count_comparison.mms_delta}})<br/>
            <span style="font-size: 12px">These figures are for the whole month and do not resepct billing start date.  Negative numbers mean RedChirp recorded more messages than we are billing for. Positive numbers mean RedChirp recorded less messages than we are billing for.</span>
        </div>

        <a v-on:click="onClickExportDetail">Export Billing Detail</a>

        <v-data-table
            v-if="invoice_preview != null"
            :headers="headers"
            :items="invoice_preview.lines"
            :items-per-page="100"
            class="elevation-1"
            :hide-default-footer="true"
            style="margin-top: 15px;"

            :footer-props="{
                disableItemsPerPage: false,
                disablePagination: false,
                }"

        >

            <template v-slot:item.has_rate_card_been_customized="{ item }">
                {{prettyPrintCustomized(item.has_rate_card_been_customized)}}
            </template>

            <template v-slot:item.intuit_company_name="{ item }">
                {{prettyPrintCompanyName(item)}}
            </template>

        </v-data-table>

        <div
            v-if="invoice_preview != null"
            style="text-align: right; padding-top: 15px; padding-bottom: 15px; font-weight: 700; font-size: 24px;"
        >
            Total: ${{pretty_total}}
        </div>

        <div
            style="text-align: right; padding-top: 15px; padding-bottom: 15px;"
            v-if="invoice_preview != null"
        >
            <v-btn
                v-if="invoice_preview.view_invoice_url != null"
                v-on:click="onClickViewInQuickBooks"
            >
                View In QuickBooks Online
            </v-btn>

            <v-btn
                v-if="invoice_preview.intuit_invoice_upload_record == null && invoice_preview.intuit_customer_id != null"
                color="primary"
                v-on:click="onClickUpsertInvoice"
                style="margin-left: 15px;"
            >
                Create Invoice
            </v-btn>

            <v-btn
                v-if="invoice_preview.intuit_invoice_upload_record != null && invoice_preview.intuit_customer_id != null"
                color="primary"
                v-on:click="onClickUpsertInvoice"
                style="margin-left: 15px;"
            >
                Update Invoice #{{invoice_preview.intuit_invoice_upload_record.intuit_invoice_number}}
            </v-btn>

            <div
                v-if="invoice_preview.intuit_customer_id == null"
            >
                <strong>You must set the Intuit customer name before you can create an invoice.</strong>
            </div>
            
        </div>
    </div>


    <m-admin-billing-credit-edit-dialog
        ref="admin-billing-credit-edit-dialog"
        v-on:ok="onChangeCredit"
    />

</v-container>
</m-pageframe>
</template>

<script>
export default 
{
    data() {
        return {
            rate_card: null,
            selected_month: null,
            invoice_preview: null,
            msg_count_comparison: null,

            headers: [
                { 'text' : 'SKU', 'value' : 'sku' },
                { 'text' : 'Description', 'value' : 'description' },
                { 'text' : 'QTY' , 'value' : 'qty' },
                { 'text' : 'Rate' , 'value' : 'rate' },
                { 'text' : 'Amount' , 'value' : 'amount' },
            ],
        }
    },

    mounted () {
        this.update();
    },

    methods: {
        update()
        {
            this.$rc.rest({
                'spinner': true,
                'endpoint': 'GET: /api/v1/billing/organization/rate-card/:organization_id',
            
                'params' : {
                        'organization_id': this.$route.params.organization_id,
                    },
            
                'on_success': (data) => {
                        this.rate_card = data;
                        this.updateInvoicePreview();
                    },
            
                'on_error': 'Unable to load rate card',
            });
        },

       
        onClickEditRateCard()
        {
            this.$router.push({
                'name': 'edit-organization-rate-card', 
                params: { 
                  'organization_id' : this.rate_card.organization_id,
                }
            })
            .catch(e=>{}); // swallow any exception
        },

        onClickEditIntuitCustomer()
        {
            this.$refs['search-customers-dialog'].showDialog();
        },

        onSetCustomerEmails(value)
        {
            this.$rc.rest({
                'spinner': true,
                'endpoint': 'PUT: /api/v1/validate/email-addresses',
            
                'params' : {
                        'emails': value,
                    },
            
                'on_success': (data) => {
                        if ( !data.is_valid )
                        {
                            this.$rc.alert("Invalid email addresses", "The email addresses you have entered are not valid.<br/><br/>No changes have been made.");
                            return;
                        }

                        let patch_data = {
                            'organization_id': this.rate_card.organization_id,
                            'customer_emails_unparsed': data.comma,
                        };

                        this.patch(patch_data);
                    },
            
                'on_error': 'Unable to validate email addresses',
            });
        },

        onSetIntuitCustomer(intuit_customer)
        {
            let patch_data = {
                'organization_id': this.rate_card.organization_id,
                'intuit_customer_id': intuit_customer.id,
                'intuit_company_name': intuit_customer.company_name,
            };

            this.patch(patch_data);
        },
        

        onSetBillingStartDate(date)
        {
            let patch_data = {
                'organization_id': this.rate_card.organization_id,
                'override_billing_start_date': date,
                'unset_billing_start_date': date == null,
            };

            this.patch(patch_data);
        },

        onSetInvoiceException(value)
        {
            let patch_data = {
                'organization_id': this.rate_card.organization_id,
                'invoice_exception': value,
            };

            this.patch(patch_data);
        },

        onSetInvoiceExceptionNote(value)
        {
            let patch_data = {
                'organization_id': this.rate_card.organization_id,
                'invoice_exception_note': value,
            };

            this.patch(patch_data);
        },

        onSetPaymentMethod(method)
        {
            let required_payment_terms = 'due-on-receipt';
            if ( method.value == 'check' ) required_payment_terms = 'net-30';

            let change_payment_terms = this.rate_card.selected_rate_card.payment_terms != required_payment_terms;

            let patch_data = {
                'organization_id': this.rate_card.organization_id,
                'payment_method': method.value,
                'payment_terms' : required_payment_terms,
            };

            this.patch(patch_data);
            this.$rc.snackbar('Updated payment terms and collection fee to match payment method.',2000);
        },

        onChangeCredit(new_rate_card)
        {
            this.rate_card.selected_rate_card = new_rate_card;

            let patch_data = {
                'organization_id': this.rate_card.organization_id,
                'credit_setup': new_rate_card.credit_setup,
                'credit_recurring_fee' : new_rate_card.credit_recurring_fee,
            };

            this.patch(patch_data);
        },


        onSetCustomRateCardLabel(value)
        {
            let patch_data = {
                'organization_id': this.rate_card.organization_id,
                'custom_rate_card_label': value,
            };

            this.patch(patch_data);
        },

        onSetPONumber(value)
        {
            let patch_data = {
                'organization_id': this.rate_card.organization_id,
                'po_number': value,
            };

            this.patch(patch_data);
        },

        onSetFreeTrialDesc(value)
        {
            let patch_data = {
                'organization_id': this.rate_card.organization_id,
                'free_trial_desc': value,
            };

            this.patch(patch_data);
        },


        onSetPaymentTerms(terms)
        {
            let patch_data = {
                'organization_id': this.rate_card.organization_id,
                'payment_terms': terms.value,
            };

            this.patch(patch_data);
        },

        patch(patch_data)
        {
            this.$rc.rest({
                'spinner': true,
                'endpoint': 'PATCH: /api/v1/billing/organization',
            
                'params' : patch_data,
            
                'on_success': (data) => {
                        this.update();
                    },
            
                'on_error': 'Unable to update organization settings',
            });
        },

        onClickExportDetail()
        {
            this.$rc.excelExport(
                    'EXPORT: /api/v1/billing/start-excel-export-billing-details/:organization_id/:month',
                    {
                        'organization_id': this.rate_card.organization_id,
                        'month': this.selected_month,
                    }
                );
        },

        updateInvoicePreview()
        {
            if ( this.rate_card == null || this.selected_month == null )
                return;

            this.$rc.rest({
                'spinner': true,
                'endpoint': 'PUT: /api/v1/billing/organization/preview-invoice',
            
                'params' : {
                        'organization_id': this.rate_card.organization_id,
                        'invoice_month': this.selected_month,
                    },
            
                'on_success': (data) => {
                        this.invoice_preview = data;
                    },
            
                'on_error': 'Unable to preview invoice',
            });

            if ( this.selected_month != null )
            {
                this.$rc.rest({
                    'spinner': false,
                    'endpoint': 'GET: /api/v1/billing/compare-stored-to-log-messages/:organization_id/:invoice_month',
                
                    'params' : {
                            'organization_id': this.$route.params.organization_id,
                            'invoice_month': this.selected_month,
                        },
                
                    'on_success': (data) => {
                            this.msg_count_comparison = data;
                        },
                
                    'on_error': () => { this.msg_count_comparison = null; },
                });
            }
            else
            {
                this.msg_count_comparison = null;
            }
        },

        onClickViewInQuickBooks()
        {
            window.open(this.invoice_preview.view_invoice_url,"_blank");
        },

        onClickUpsertInvoice()
        {
            this.$rc.rest({
                'spinner': true,
                'endpoint': 'upsert-invoice: /api/v1/billing/organization',
                'timeout': 5*60000, // 5 minutes
            
                'params' : {
                        'organization_id': this.rate_card.organization_id,
                        'invoice_month': this.selected_month,
                    },
            
                'on_success': (data) => {
                        this.updateInvoicePreview();
                        this.$rc.snackbar('Invoice updated');
                    },
            
                'on_error': 'Unable to create invoice in QuickBooks.  For details, search the rprod logs for HandlerUpsertInvoice (-B 10 -A 20) -- timeout set to 5 minutes',
            });
        },

        onClickEditCredit()
        {
            this.$refs['admin-billing-credit-edit-dialog'].showDialog(this.rate_card.selected_rate_card);
        },
    },

    computed: {
        rate_card_label() {
            if ( this.rate_card == null ) return '';

            let label = this.rate_card.selected_rate_card.rate_card_label;

            if ( this.rate_card.selected_rate_card.has_rate_card_been_customized )
                return label+' (Customized)';
            
            return label;
        },

        intuit_company_name() {
            if ( this.rate_card == null ) return '';

            let item = this.rate_card.selected_rate_card;

            if ( item.intuit_customer_id == null ) return '';
            if ( item.intuit_company_name == null ) return '('+item.intuit_customer_id+')';

            return item.intuit_company_name+' ('+item.intuit_customer_id+')';
        },

        pretty_total() {
            if ( this.invoice_preview == null ) return '';

            let total = parseFloat(this.invoice_preview.total);

            if (isNaN(total) ) 
                return ''

            total = total.toFixed(2);

            return total.toLocaleString();    

        },

        pretty_customer_emails()
        {
            let values = this.$rc.getDeep(this.rate_card, 'selected_rate_card/send_invoice_to_customer_emails');
            if ( values == null ) return null;

            return this.$rc.pretty(values,'comma_list');
        },

        is_finance()
        {
            return this.$rc.$state.authAccountHasRedChirpStaffRole('finance');
        },
    },
}
</script>

<style lang="scss" scoped>
</style>